// src/components/GestionTableros.js (Versión Completa con Modo Espera)
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  TextField,
  Typography,
  Box,
  Grid,
  Tabs,
  Tab,
  Stack,
  Chip,
  Tooltip,
  useMediaQuery,
  useTheme,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  FormatSize as FormatSizeIcon,
  ColorLens as ColorLensIcon,
  Style as StyleIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  HourglassEmpty as WaitingIcon,
} from '@mui/icons-material';

// Importar el hook del contexto
import { useEventContext } from '../../context/EventContext';

// Importar los componentes de pestañas de TableroPrincipal
import TabPanel from '../Tableros/common/TabPanel';
import GeneralTab from '../Tableros/tabs/GeneralTab';
import ColumnasTab from '../Tableros/tabs/ColumnasTab';
import TipografiaTab from '../Tableros/tabs/TipografiaTab';
import ColoresTab from '../Tableros/tabs/ColoresTab';
import TemasTab from '../Tableros/tabs/TemasTab';

// Importar temas
import { themes } from '../Tableros/constants/themes';

const GestionTableros = () => {
  // Obtener datos y funciones del contexto
  const {
    boardConfigs,
    updateBoardConfig,
    deleteBoardConfig,
    evtData, 
  } = useEventContext();

  // Estado para Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Estado para manejar ventanas abiertas
  const [openWindows, setOpenWindows] = useState({});

  // Estado para manejar el modo espera de cada tablero
  const [waitModeStates, setWaitModeStates] = useState({});

  // Función para mostrar Snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Función para alternar el modo espera
  const toggleWaitMode = (configId) => {
    const currentState = waitModeStates[configId] || false;
    const newState = !currentState;
    
    setWaitModeStates(prev => ({
      ...prev,
      [configId]: newState
    }));

    // Enviar mensaje a la ventana del tablero si está abierta
    if (openWindows[configId] && !openWindows[configId].closed) {
      try {
        openWindows[configId].postMessage({
          type: 'WAIT_MODE_TOGGLE',
          isWaiting: newState
        }, window.location.origin);
        
        showSnackbar(
          newState ? 'Modo espera activado' : 'Modo espera desactivado', 
          newState ? 'warning' : 'success'
        );
      } catch (error) {
        console.error('Error enviando mensaje de modo espera:', error);
        showSnackbar('Error al cambiar modo de espera', 'error');
      }
    } else {
      showSnackbar(
        newState ? 'Modo espera activado (tablero no abierto)' : 'Modo espera desactivado', 
        'info'
      );
    }

    // Guardar estado en localStorage para persistencia
    localStorage.setItem(`wait-mode-${configId}`, JSON.stringify(newState));
  };

  // Función para eliminar una configuración
  const handleDelete = async (id) => {
    try {
      await deleteBoardConfig(id);
      showSnackbar('Tablero eliminado', 'info');
      
      // Cerrar ventana si está abierta
      if (openWindows[id]) {
        openWindows[id].close();
        setOpenWindows(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }

      // Limpiar estado de espera
      setWaitModeStates(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });

      // Limpiar localStorage
      localStorage.removeItem(`wait-mode-${id}`);
    } catch (error) {
      console.error('Error eliminando tablero:', error);
      showSnackbar('Error al eliminar tablero', 'error');
    }
  };

  // Función para abrir tablero en nueva ventana
  const openTablero = (configId) => {
    const config = boardConfigs.find(c => c.id === configId);
    if (!config) {
      showSnackbar('Configuración no encontrada', 'error');
      return;
    }

    // Guardar configuración en localStorage para que la nueva ventana pueda accederla
    localStorage.setItem(`config-${configId}`, JSON.stringify(config));

    // Calcular dimensiones de la ventana
    const tableWidth = config.settings?.tableWidth || 1000;
    const tableHeight = config.settings?.tableHeight || 1200;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const width = Math.min(tableWidth + 40, screenWidth);
    const height = Math.min(tableHeight + 40, screenHeight);

    // Abrir nueva ventana
    const newWindow = window.open(
      `/scoreboard?id=${configId}&type=${config.boardType || 'track'}`,
      `tablero-${configId}`,
      `width=${width},height=${height},menubar=no,toolbar=no,location=no,status=no`
    );

    if (newWindow) {
      setOpenWindows(prev => ({ ...prev, [configId]: newWindow }));
      showSnackbar('Tablero abierto en nueva ventana', 'success');

      // ==== HANDSHAKE PARA ENVIAR eventData ====
      const handler = (event) => {
        if (event.source === newWindow
            && event.data?.type === 'SCOREBOARD_READY'
            && event.data.configId === configId) {
          // Recalcular eventData igual que al abrir
          let eventData = null;
          const assigned = config.settings?.assignedHeat;
          if (assigned) {
            const [evtNum, rnd, h] = assigned.split('_').map(Number);
            const ev = evtData.find(e =>
              +e.eventNumber === evtNum &&
              +e.round       === rnd &&
              +e.heat        === h
            );
            if (ev) {
              eventData = {
                eventName:     ev.eventName,
                eventSubtitle: ev.eventSubtitle || '',
                eventNumber:   evtNum,
                round:         rnd,
                heat:          h,
                participants:  (ev.participants || []).map(p => ({
                 ...p,
                 fullName: p.fullName 
                   || [p.firstName, p.lastName].filter(Boolean).join(' ')
               }))
              };
            }
          }
          newWindow.postMessage({
            type:   'INITIAL_CONFIG',
            config: { ...config, eventData },
            isWaiting: waitModeStates[configId] || false
          }, window.location.origin);
          window.removeEventListener('message', handler);
        }
      };
      window.addEventListener('message', handler);

    } else {
      showSnackbar('Permite ventanas emergentes para abrir el tablero', 'warning');
    }
  };

  // Función para actualizar tableros abiertos
  const refreshTablero = (configId) => {
    const config = boardConfigs.find(c => c.id === configId);
    if (!config) return false;

    // Actualizar localStorage
    localStorage.setItem(`config-${configId}`, JSON.stringify(config));

    // Si hay ventana abierta, enviar mensaje para actualizar
    if (openWindows[configId] && !openWindows[configId].closed) {
      try {
        // Recalcular el mismo eventData que en INITIAL_CONFIG
        let eventData = null;
        const assigned = config.settings?.assignedHeat;
        if (assigned) {
          const [evtNum, rnd, h] = assigned.split('_').map(Number);
          const ev = evtData.find(e =>
            +e.eventNumber === evtNum &&
            +e.round       === rnd &&
            +e.heat        === h
          );
          if (ev) {
            eventData = {
              eventName:     ev.eventName,
              eventSubtitle: ev.eventSubtitle || '',
              eventNumber:   evtNum,
              round:         rnd,
              heat:          h,
              participants:  (ev.participants || []).map(p => ({
                ...p,
                fullName: p.fullName 
                  || [p.firstName, p.lastName].filter(Boolean).join(' ')
              }))
            };
          }
        }

        openWindows[configId].postMessage({
          type:   'CONFIG_UPDATE',
          config: { ...config, eventData },
          isWaiting: waitModeStates[configId] || false
        }, window.location.origin);
        return true;
      } catch (error) {
        console.error('Error enviando mensaje a ventana:', error);
        return false;
      }
    }

    return false;
  };

  // Cargar estados de espera desde localStorage al iniciar
  useEffect(() => {
    const loadedWaitStates = {};
    boardConfigs.forEach(config => {
      const savedState = localStorage.getItem(`wait-mode-${config.id}`);
      if (savedState) {
        try {
          loadedWaitStates[config.id] = JSON.parse(savedState);
        } catch (e) {
          console.warn('Error loading wait state for', config.id);
        }
      }
    });
    setWaitModeStates(loadedWaitStates);
  }, [boardConfigs]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        Gestión de Tableros
      </Typography>
      
      {boardConfigs.length === 0 ? (
        <Card sx={{ textAlign: 'center', p: 4, backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6" color="textSecondary">
            No hay tableros configurados
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Ve a "Tablero Principal" para crear y guardar configuraciones de tableros
          </Typography>
        </Card>
      ) : (
        <Stack spacing={3}>
          {boardConfigs.map((config) => (
            <ConfigCard
              key={config.id}
              config={config}
              updateBoardConfig={updateBoardConfig}
              handleDelete={handleDelete}
              openTablero={openTablero}
              refreshTablero={refreshTablero}
              openWindows={openWindows}
              isMobile={isMobile}
              showSnackbar={showSnackbar}
              themes={themes}
              waitModeStates={waitModeStates}
              toggleWaitMode={toggleWaitMode}
            />
          ))}
        </Stack>
      )}
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const ConfigCard = ({ 
  config, 
  updateBoardConfig,
  handleDelete, 
  openTablero, 
  refreshTablero,
  openWindows, 
  isMobile,
  showSnackbar,
  themes,
  waitModeStates,
  toggleWaitMode
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [draftConfig, setDraftConfig] = useState(config);
  const [expanded, setExpanded] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [boardType, setBoardType] = useState(config.boardType || 'track');
  
  const isWaiting = waitModeStates[config.id] || false;
  const isWindowOpen = openWindows[config.id] && !openWindows[config.id].closed;
  
  const settingsFields = [
    'tableWidth', 'tableHeight', 'displayRows', 'stopwatchSize', 'largeStopwatchSize',
    'headerFontSize', 'nameFontSize', 'positionFontSize', 'timeFontSize', 'resultFontSize',
    'attemptFontSize', 'teamFontSize', 'lapTimeFontSize', 'reactionTimeFontSize', 
    'differenceTimeFontSize', 'headerFontFamily', 'nameFontFamily', 'numbersFontFamily',
    'tableBackgroundColor', 'globalFontColor', 'timerColor', 'largeTimerColor',
    'headerFontColor', 'headerBackgroundColor', 'columnsHeaderColor', 'athleteNameColor',
    'teamNameColor', 'resultTimeColor', 'deltaTimeColor', 'positionColor', 
    'positionBackgroundColor', 'nameBackgroundColor', 'laneColor', 'numberColor',
    'markBackgroundColor', 'attemptColor', 'attemptBackgroundColor', 'lastLapTimeColor',
    'reactionTimeColor', 'lapsToGoColor', 'lapsColumnsColor', 'tableRowOddColor',
    'tableRowEvenColor', 'successColor', 'failColor', 'passColor',
    'showLane', 'showNumber', 'showLastLapTime', 'showLapsToGo', 'showLapsColumns',
    'showReactionTime', 'showTimeDifference', 'showAttemptNumber', 'assignedHeat'
  ];
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditChange = (field, value) => {
    setDraftConfig(prev => {
      if (settingsFields.includes(field)) {
        return {
          ...prev,
          settings: { 
            ...prev.settings, 
            [field]: value 
          }
        };
      }
      return { 
        ...prev, 
        [field]: value 
      };
    });
    setHasPendingChanges(true);
  };

  const handleRefresh = async () => {
    try {
      await updateBoardConfig(config.id, draftConfig);
      const updated = refreshTablero(config.id);
      if (updated) {
        showSnackbar('Tablero refrescado con éxito', 'success');
      } else {
        showSnackbar('Configuración guardada, pero el tablero no está abierto', 'warning');
      }
      setHasPendingChanges(false);
    } catch (err) {
      console.error('Error al actualizar configuración:', err);
      showSnackbar('Error al actualizar configuración', 'error');
    }
  };

  const handleBoardTypeChange = async (type) => {
    setBoardType(type);
    handleEditChange('boardType', type);
    showSnackbar(`Tipo de tablero cambiado a ${type}`, 'info');
  };

  const openCurrentTablero = () => {
    openTablero(config.id);
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    handleEditChange(name, checked);
  };

  const handleDisplayRowsChange = (event) => {
    const value = Math.max(1, parseInt(event.target.value) || 1);
    handleEditChange('displayRows', value);
  };

  useEffect(() => {
    setDraftConfig(config);
    setBoardType(config.boardType || 'track');
    setHasPendingChanges(false);
  }, [config]);

  const resetToDefault = async () => {
    try {
      handleEditChange('tableWidth', 1000);
      handleEditChange('tableHeight', 1200);
      handleEditChange('displayRows', 8);
      handleEditChange('stopwatchSize', 50);
      handleEditChange('largeStopwatchSize', 120);
      showSnackbar('Valores generales restaurados a los predeterminados', 'success');
    } catch (error) {
      showSnackbar('Error al restaurar valores predeterminados', 'error');
    }
  };

  const resetToDefaultColors = async () => {
    try {
      const defaultColors = {
        tableBackgroundColor: '#1a1a1a',
        globalFontColor: '#ffffff',
        timerColor: '#00ff00',
        largeTimerColor: '#00ff00',
        headerBackgroundColor: '#2c0369',
        headerFontColor: '#ffffff',
        columnsHeaderColor: '#4c516d',
        athleteNameColor: '#ffffff',
        teamNameColor: '#e0e0e0',
        resultTimeColor: '#ffffff',
        deltaTimeColor: '#c1ff30',
        positionColor: '#ffffff',
        positionBackgroundColor: '#4c516d',
        nameBackgroundColor: '#515e7b',
        tableRowOddColor: '#252525',
        tableRowEvenColor: '#1e1e1e',
        successColor: '#1b5e20',
        failColor: '#b71c1c',
        passColor: '#0d47a1'
      };

      Object.entries(defaultColors).forEach(([key, value]) => {
        handleEditChange(key, value);
      });
      
      showSnackbar('Colores restaurados a los predeterminados', 'success');
    } catch (error) {
      showSnackbar('Error al restaurar colores predeterminados', 'error');
    }
  };

  const tabProps = {
    tableWidth: draftConfig.settings?.tableWidth || 1000,
    setTableWidth: (value) => handleEditChange('tableWidth', value),
    tableHeight: draftConfig.settings?.tableHeight || 1200,
    setTableHeight: (value) => handleEditChange('tableHeight', value),
    tableBackgroundColor: draftConfig.settings?.tableBackgroundColor || '#1a1a1a',
    setTableBackgroundColor: (value) => handleEditChange('tableBackgroundColor', value),
    globalFontColor: draftConfig.settings?.globalFontColor || '#ffffff',
    setGlobalFontColor: (value) => handleEditChange('globalFontColor', value),
    timerColor: draftConfig.settings?.timerColor || '#00ff00',
    setTimerColor: (value) => handleEditChange('timerColor', value),
    largeTimerColor: draftConfig.settings?.largeTimerColor || '#00ff00',
    setLargeTimerColor: (value) => handleEditChange('largeTimerColor', value),
    headerFontSize: draftConfig.settings?.headerFontSize || 24,
    setHeaderFontSize: (value) => handleEditChange('headerFontSize', value),
    nameFontSize: draftConfig.settings?.nameFontSize || 18,
    setNameFontSize: (value) => handleEditChange('nameFontSize', value),
    positionFontSize: draftConfig.settings?.positionFontSize || 20,
    setPositionFontSize: (value) => handleEditChange('positionFontSize', value),
    timeFontSize: draftConfig.settings?.timeFontSize || 18,
    setTimeFontSize: (value) => handleEditChange('timeFontSize', value),
    resultFontSize: draftConfig.settings?.resultFontSize || 54,
    setResultFontSize: (value) => handleEditChange('resultFontSize', value),
    attemptFontSize: draftConfig.settings?.attemptFontSize || 18,
    setAttemptFontSize: (value) => handleEditChange('attemptFontSize', value),
    teamFontSize: draftConfig.settings?.teamFontSize || 24,
    setTeamFontSize: (value) => handleEditChange('teamFontSize', value),
    lapTimeFontSize: draftConfig.settings?.lapTimeFontSize || 18,
    setLapTimeFontSize: (value) => handleEditChange('lapTimeFontSize', value),
    reactionTimeFontSize: draftConfig.settings?.reactionTimeFontSize || 16,
    setReactionTimeFontSize: (value) => handleEditChange('reactionTimeFontSize', value),
    differenceTimeFontSize: draftConfig.settings?.differenceTimeFontSize || 16,
    setDifferenceTimeFontSize: (value) => handleEditChange('differenceTimeFontSize', value),
    headerFontFamily: draftConfig.settings?.headerFontFamily || 'Roboto',
    setHeaderFontFamily: (value) => handleEditChange('headerFontFamily', value),
    nameFontFamily: draftConfig.settings?.nameFontFamily || 'Roboto',
    setNameFontFamily: (value) => handleEditChange('nameFontFamily', value),
    numbersFontFamily: draftConfig.settings?.numbersFontFamily || 'Roboto Mono',
    setNumbersFontFamily: (value) => handleEditChange('numbersFontFamily', value),
    stopwatchSize: draftConfig.settings?.stopwatchSize || 50,
    setStopwatchSize: (value) => handleEditChange('stopwatchSize', value),
    largeStopwatchSize: draftConfig.settings?.largeStopwatchSize || 120,
    setLargeStopwatchSize: (value) => handleEditChange('largeStopwatchSize', value),
    headerFontColor: draftConfig.settings?.headerFontColor || '#ffffff',
    setHeaderFontColor: (value) => handleEditChange('headerFontColor', value),
    headerBackgroundColor: draftConfig.settings?.headerBackgroundColor || '#2c0369',
    setHeaderBackgroundColor: (value) => handleEditChange('headerBackgroundColor', value),
    columnsHeaderColor: draftConfig.settings?.columnsHeaderColor || '#4c516d',
    setColumnsHeaderColor: (value) => handleEditChange('columnsHeaderColor', value),
    athleteNameColor: draftConfig.settings?.athleteNameColor || '#ffffff',
    setAthleteNameColor: (value) => handleEditChange('athleteNameColor', value),
    teamNameColor: draftConfig.settings?.teamNameColor || '#e0e0e0',
    setTeamNameColor: (value) => handleEditChange('teamNameColor', value),
    resultTimeColor: draftConfig.settings?.resultTimeColor || '#ffffff',
    setResultTimeColor: (value) => handleEditChange('resultTimeColor', value),
    deltaTimeColor: draftConfig.settings?.deltaTimeColor || '#c1ff30',
    setDeltaTimeColor: (value) => handleEditChange('deltaTimeColor', value),
    positionColor: draftConfig.settings?.positionColor || '#ffffff',
    setPositionColor: (value) => handleEditChange('positionColor', value),
    positionBackgroundColor: draftConfig.settings?.positionBackgroundColor || '#4c516d',
    setPositionBackgroundColor: (value) => handleEditChange('positionBackgroundColor', value),
    nameBackgroundColor: draftConfig.settings?.nameBackgroundColor || '#515e7b',
    setNameBackgroundColor: (value) => handleEditChange('nameBackgroundColor', value),
    laneColor: draftConfig.settings?.laneColor || '#ffffff',
    setLaneColor: (value) => handleEditChange('laneColor', value),
    numberColor: draftConfig.settings?.numberColor || '#ffffff',
    setNumberColor: (value) => handleEditChange('numberColor', value),
    markBackgroundColor: draftConfig.settings?.markBackgroundColor || '#000000',
    setMarkBackgroundColor: (value) => handleEditChange('markBackgroundColor', value),
    attemptColor: draftConfig.settings?.attemptColor || '#ffffff',
    setAttemptColor: (value) => handleEditChange('attemptColor', value),
    attemptBackgroundColor: draftConfig.settings?.attemptBackgroundColor || '#0d47a1',
    setAttemptBackgroundColor: (value) => handleEditChange('attemptBackgroundColor', value),
    lastLapTimeColor: draftConfig.settings?.lastLapTimeColor || '#ffffff',
    setLastLapTimeColor: (value) => handleEditChange('lastLapTimeColor', value),
    reactionTimeColor: draftConfig.settings?.reactionTimeColor || '#ffffff',
    setReactionTimeColor: (value) => handleEditChange('reactionTimeColor', value),
    lapsToGoColor: draftConfig.settings?.lapsToGoColor || '#ffffff',
    setLapsToGoColor: (value) => handleEditChange('lapsToGoColor', value),
    lapsColumnsColor: draftConfig.settings?.lapsColumnsColor || '#ffffff',
    setLapsColumnsColor: (value) => handleEditChange('lapsColumnsColor', value),
    tableRowOddColor: draftConfig.settings?.tableRowOddColor || '#252525',
    setTableRowOddColor: (value) => handleEditChange('tableRowOddColor', value),
    tableRowEvenColor: draftConfig.settings?.tableRowEvenColor || '#1e1e1e',
    setTableRowEvenColor: (value) => handleEditChange('tableRowEvenColor', value),
    successColor: draftConfig.settings?.successColor || '#1b5e20',
    setSuccessColor: (value) => handleEditChange('successColor', value),
    failColor: draftConfig.settings?.failColor || '#b71c1c',
    setFailColor: (value) => handleEditChange('failColor', value),
    passColor: draftConfig.settings?.passColor || '#0d47a1',
    setPassColor: (value) => handleEditChange('passColor', value),
    showLane: draftConfig.settings?.showLane || false,
    setShowLane: (value) => handleEditChange('showLane', value),
    showNumber: draftConfig.settings?.showNumber || false,
    setShowNumber: (value) => handleEditChange('showNumber', value),
    showLastLapTime: draftConfig.settings?.showLastLapTime || false,
    setShowLastLapTime: (value) => handleEditChange('showLastLapTime', value),
    showLapsToGo: draftConfig.settings?.showLapsToGo || false,
    setShowLapsToGo: (value) => handleEditChange('showLapsToGo', value),
    showLapsColumns: draftConfig.settings?.showLapsColumns || false,
    setShowLapsColumns: (value) => handleEditChange('showLapsColumns', value),
    showReactionTime: draftConfig.settings?.showReactionTime || false,
    setShowReactionTime: (value) => handleEditChange('showReactionTime', value),
    showTimeDifference: draftConfig.settings?.showTimeDifference || false,
    setShowTimeDifference: (value) => handleEditChange('showTimeDifference', value),
    showAttemptNumber: draftConfig.settings?.showAttemptNumber !== false,
    setShowAttemptNumber: (value) => handleEditChange('showAttemptNumber', value),
    displayRows: draftConfig.settings?.displayRows || 8,
    setDisplayRows: (value) => handleEditChange('displayRows', value),
    handleEditChange,
    handleCheckboxChange,
    handleDisplayRowsChange,
    showSnackbar,
    resetToDefault,
    resetToDefaultColors,
    openCurrentTablero,
    isMobile,
    boardType,
    setBoardType: handleBoardTypeChange,
  };

  const temasProps = {
    themes,
    applyTheme: async (themeName) => {
      const theme = themes[themeName];
      if (!theme) return;
      
      try {
        Object.entries(theme).forEach(([key, value]) => {
          handleEditChange(key, value);
        });
        
        showSnackbar(`Tema "${themeName}" aplicado`, 'success');
      } catch (error) {
        showSnackbar('Error al aplicar tema', 'error');
      }
    },
    tableBackgroundColor: draftConfig.settings?.tableBackgroundColor || '#1a1a1a',
    globalFontColor: draftConfig.settings?.globalFontColor || '#ffffff',
    timerColor: draftConfig.settings?.timerColor || '#00ff00',
    headerFontColor: draftConfig.settings?.headerFontColor || '#ffffff',
    athleteNameColor: draftConfig.settings?.athleteNameColor || '#ffffff',
    teamNameColor: draftConfig.settings?.teamNameColor || '#e0e0e0',
    resultTimeColor: draftConfig.settings?.resultTimeColor || '#ffffff',
    deltaTimeColor: draftConfig.settings?.deltaTimeColor || '#c1ff30',
    positionColor: draftConfig.settings?.positionColor || '#ffffff',
    tableRowOddColor: draftConfig.settings?.tableRowOddColor || '#252525',
    tableRowEvenColor: draftConfig.settings?.tableRowEvenColor || '#1e1e1e',
    showSnackbar,
    isMobile,
  };

  return (
    <Card elevation={2} sx={{ overflow: 'visible', '&:hover': { boxShadow: 6 } }}>
      <CardHeader
        title={
          <TextField
            value={draftConfig.name}
            onChange={e => handleEditChange('name', e.target.value)}
            variant="standard"
            fullWidth
            sx={{ '& input': { fontSize: '1.2rem', fontWeight: 'bold' } }}
          />
        }
        action={
          <Stack direction="row" spacing={1}>
            <Tooltip title="Abrir tablero">
              <IconButton onClick={() => openTablero(config.id)}>
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
            
            {/* Botón de modo espera */}
            <Tooltip title={isWaiting ? 'Modo espera activo' : 'Modo espera inactivo'}>
              <IconButton
                color={isWaiting ? 'warning' : 'success'}
                onClick={() => toggleWaitMode(config.id)}
                sx={{
                  backgroundColor: isWaiting 
                    ? 'rgba(255, 152, 0, 0.15)' 
                    : 'rgba(76, 175, 80, 0.15)',
                  '&:hover': {
                    backgroundColor: isWaiting 
                      ? 'rgba(255, 152, 0, 0.25)' 
                      : 'rgba(76, 175, 80, 0.25)',
                  },
                  animation: isWaiting ? 'waitPulse 2s infinite' : 'none',
                  '@keyframes waitPulse': {
                    '0%': { 
                      transform: 'scale(1)',
                      boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.7)'
                    },
                    '50%': { 
                      transform: 'scale(1.05)',
                      boxShadow: '0 0 0 4px rgba(255, 152, 0, 0)'
                    },
                    '100%': { 
                      transform: 'scale(1)',
                      boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)'
                    }
                  }
                }}
              >
                {isWaiting ? <WaitingIcon /> : <PlayArrowIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Actualizar tablero">
              <IconButton
                color="info"
                onClick={handleRefresh}
                sx={{
                  backgroundColor: hasPendingChanges
                    ? 'rgba(3,169,244,0.15)'
                    : 'rgba(3,169,244,0.08)',
                  animation: hasPendingChanges ? 'refreshPulse 1.5s infinite' : 'none',
                  '@keyframes refreshPulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(3,169,244,0.5)' },
                    '70%': { boxShadow: '0 0 0 6px rgba(3,169,244,0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(3,169,244,0)' }
                  }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Eliminar tablero">
              <IconButton color="error" onClick={() => handleDelete(config.id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={expanded ? 'Contraer' : 'Expandir'}>
              <IconButton 
                onClick={() => setExpanded(x => !x)}
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out'
                }}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        }
        subheader={
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2} mt={1}>
            <Chip label={draftConfig.boardType || 'track'} size="small" />
            <Chip label={`${draftConfig.settings?.displayRows || 8} Filas`} size="small" />
            <Chip
              label={`${draftConfig.settings?.tableWidth || 1000}×${draftConfig.settings?.tableHeight || 1200}`}
              size="small"
            />
            
            {/* Chip de estado de asignación */}
            {draftConfig.settings?.assignedHeat ? (
              <Chip 
                label={`Asignado: ${draftConfig.settings.assignedHeat.replace(/_/g, '-')}`}
                size="small" 
                color="success"
                variant="filled"
              />
            ) : (
              <Chip 
                label="Sin asignar" 
                size="small" 
                color="default"
                variant="outlined"
              />
            )}

            {/* Chip de estado de espera */}
            {isWaiting && (
              <Chip 
                label="En modo espera" 
                size="small" 
                color="warning"
                variant="filled"
                icon={<WaitingIcon fontSize="small" />}
                sx={{
                  animation: 'chipPulse 2s infinite',
                  '@keyframes chipPulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.7 },
                    '100%': { opacity: 1 }
                  }
                }}
              />
            )}

            {/* Chips de colores de tema */}
            <Stack direction="row" spacing={0.5}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: draftConfig.settings?.tableBackgroundColor || '#1a1a1a',
                  border: '1px solid #ccc',
                  display: 'inline-block'
                }}
                title="Color de fondo"
              />
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: draftConfig.settings?.globalFontColor || '#ffffff',
                  border: '1px solid #ccc',
                  display: 'inline-block'
                }}
                title="Color de fuente"
              />
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: draftConfig.settings?.timerColor || '#00ff00',
                  border: '1px solid #ccc',
                  display: 'inline-block'
                }}
                title="Color del cronómetro"
              />
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: draftConfig.settings?.headerBackgroundColor || '#2c0369',
                  border: '1px solid #ccc',
                  display: 'inline-block'
                }}
                title="Color del encabezado"
              />
            </Stack>

            {/* Indicador de ventana abierta */}
            {isWindowOpen && (
              <Chip 
                label="Ventana abierta" 
                size="small" 
                color="info"
                variant="filled"
                icon={<OpenInNewIcon fontSize="small" />}
              />
            )}

            {hasPendingChanges && (
              <Chip 
                label="Cambios pendientes" 
                size="small" 
                color="warning"
                variant="outlined"
                sx={{
                  animation: 'pendingPulse 1.5s infinite',
                  '@keyframes pendingPulse': {
                    '0%': { borderColor: 'rgba(237, 108, 2, 0.5)' },
                    '50%': { borderColor: 'rgba(237, 108, 2, 1)' },
                    '100%': { borderColor: 'rgba(237, 108, 2, 0.5)' }
                  }
                }}
              />
            )}
          </Stack>
        }
      />

      {expanded && (
        <>
          <Divider />
          <Box borderBottom={1} borderColor="divider">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 48,
                  fontSize: '0.875rem'
                }
              }}
            >
              <Tab 
                icon={<SettingsIcon fontSize="small" />}
                label="General" 
                iconPosition="start"
              />
              <Tab 
                icon={<VisibilityIcon fontSize="small" />}
                label="Columnas" 
                iconPosition="start"
              />
              <Tab 
                icon={<FormatSizeIcon fontSize="small" />}
                label="Tipografía" 
                iconPosition="start"
              />
              <Tab 
                icon={<ColorLensIcon fontSize="small" />}
                label="Colores" 
                iconPosition="start"
              />
              <Tab 
                icon={<StyleIcon fontSize="small" />}
                label="Temas" 
                iconPosition="start"
              />
            </Tabs>
          </Box>
          <CardContent sx={{ p: 0 }}>
            <TabPanel value={activeTab} index={0}>
              <GeneralTab {...tabProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
              <ColumnasTab {...tabProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
              <TipografiaTab {...tabProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={3}>
              <ColoresTab {...tabProps} />
            </TabPanel>
            <TabPanel value={activeTab} index={4}>
              <TemasTab {...temasProps} />
            </TabPanel>
          </CardContent>
        </>
      )}
    </Card>
  );
};

export default GestionTableros;